import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { IAppConfig } from "../shared/types/config.types";
import { IApiTestParams } from "../shared/types/testing.types";
import { LOCAL_BASE_API_URL, LOCAL_BASE_URL } from "./constants/urls.constants";
import {
  CreateOutageViewModel,
  LookupsResponse,
  LookupsViewModel,
  OutagesResponse,
  OutageViewModel,
  OutageViewModelWithContacts,
  Version,
  VersionResponse,
} from "./generated/models";
import APIUtils, { EApiTypes } from "./utils.api";
import MapUtils from "../components/map/Map.utils";
import { IExtent } from "../components/map/Map.types";
import store from "../state-management/store";

interface ILookUpsAPI {
  getLookups: (
    params?: IApiTestParams | undefined
  ) => Promise<LookupsViewModel>;
}

interface IOutagesAPI {
  getOutageById: (
    outageId: number,
    params?: IApiTestParams | undefined
  ) => Promise<OutageViewModel>;
  getOutagesByExtent: (
    params?: IApiTestParams | undefined
  ) => Promise<Array<OutageViewModel>>;
  submitOutage: (
    outage: CreateOutageViewModel,
    hasImage: boolean,
    fileData?: File,
    params?: IApiTestParams | undefined
  ) => Promise<Array<OutageViewModel>>;
  searchOutages: (
    searchTerm: string,
    page_size?: number,
    page_number?: number,
    params?: IApiTestParams | undefined
  ) => Promise<Array<OutageViewModel>>;
  searchAdminOutages: (
    page_size?: number,
    page_number?: number,
    params?: IApiTestParams | undefined
  ) => Promise<Array<OutageViewModelWithContacts>>;
  updateAdminOutage: (
    outage: OutageViewModel,
    locationId?: number,
    completedReason?: string,
    params?: IApiTestParams | undefined
  ) => Promise<Array<OutageViewModel>>;
}

interface IVersionAPI {
  getVersion: (params?: IApiTestParams | undefined) => Promise<Version>;
}

export class ApiService {
  private static instance: ApiService;
  private _appConfig!: IAppConfig;
  private _lookupsApi!: ILookUpsAPI;
  private _outagesApi!: IOutagesAPI;
  private _versionApi!: IVersionAPI;
  private lookupsAxiosInstance!: AxiosInstance;
  private outagesAxiosInstance!: AxiosInstance;
  private versionAxiosInstance!: AxiosInstance;

  public getBaseAPIUrl(): string {
    let baseURL;

    if (
      process.env.NODE_ENV === "development" ||
      process.env.NODE_ENV === "test"
    ) {
      if (process.env.REACT_APP_ENABLE_LOCAL === "true") {
        baseURL = LOCAL_BASE_API_URL;
      } else {
        baseURL = this.appConfig.apiUrl;
      }
    } else {
      baseURL = this.appConfig.apiUrl;
    }

    return baseURL;
  }

  public getBaseUrl(): string {
    let baseURL;

    if (
      process.env.NODE_ENV === "development" ||
      process.env.NODE_ENV === "test"
    ) {
      baseURL = LOCAL_BASE_URL;
    } else {
      baseURL = this.appConfig.apiUrl.replace("/api", "");
    }

    return baseURL;
  }

  private buildAxiosInstance(): AxiosInstance {
    return axios.create({
      baseURL: this.getBaseAPIUrl(),
      headers: {
        "Ocp-Apim-Subscription-Key": this.appConfig.apimSubscriptionKey,
      },
    });
  }

  private initLookupsApi() {
    this.lookupsAxiosInstance = this.buildAxiosInstance();
    this._lookupsApi = {
      getLookups: async (
        params?: IApiTestParams
      ): Promise<LookupsViewModel> => {
        return this.lookupsAxiosInstance({
          method: "GET",
          url: APIUtils.getEndpoint(EApiTypes.LOOKUPS, params),
        })
          .then(function (response: AxiosResponse<LookupsResponse>) {
            return response.data;
          })
          .catch(function (error: AxiosError<LookupsResponse>) {
            return error;
          });
      },
    };
  }

  private initOutagesApi(): void {
    this.outagesAxiosInstance = this.buildAxiosInstance();
    this._outagesApi = {
      getOutageById: async (
        outageId: number,
        params?: IApiTestParams
      ): Promise<OutageViewModel> => {
        return this.outagesAxiosInstance({
          method: "GET",
          url: `${APIUtils.getEndpoint(EApiTypes.OUTAGES, params)}/${outageId}`,
        })
          .then(function (response: AxiosResponse<OutagesResponse>) {
            return response.data;
          })
          .catch(function (error: AxiosError<OutagesResponse>) {
            return error;
          });
      },
      getOutagesByExtent: async (
        params?: IApiTestParams
      ): Promise<Array<OutageViewModel>> => {
        const currentExtent = params?.currentExtent;
        let latLongExtent: IExtent;

        if (currentExtent) {
          latLongExtent = MapUtils.projectLatLngFromExtent(currentExtent);

          return this.outagesAxiosInstance({
            method: "GET",
            url: APIUtils.getEndpoint(EApiTypes.OUTAGES, params),
            params: {
              top_left_lat: latLongExtent.ymin,
              top_left_long: latLongExtent.xmax,
              bottom_right_lat: latLongExtent.ymax,
              bottom_right_long: latLongExtent.xmin,
            },
          })
            .then(function (response: AxiosResponse<OutagesResponse>) {
              return response.data;
            })
            .catch(function (error: AxiosError<OutagesResponse>) {
              return error;
            });
        } else {
          return Promise.reject();
        }
      },
      submitOutage: async (
        outage: CreateOutageViewModel,
        hasImage: boolean,
        fileData?: File,
        params?: IApiTestParams
      ): Promise<Array<OutageViewModel>> => {
        if (hasImage && fileData) {
          return this.outagesAxiosInstance({
            method: "POST",
            data: outage,
            url: APIUtils.getEndpoint(EApiTypes.OUTAGES, params),
            headers: {
              // image type will always be .jpg since we convert all files in imageUploadService
              "SOAR-UploadImage": "jpg",
            },
          })
            .then(function (response: AxiosResponse<OutagesResponse>) {
              return response.data;
            })
            .catch(function (error: AxiosError<OutagesResponse>) {
              return error;
            });
        }

        return this.outagesAxiosInstance({
          method: "POST",
          data: outage,
          url: APIUtils.getEndpoint(EApiTypes.OUTAGES, params),
        })
          .then(function (response: AxiosResponse<OutagesResponse>) {
            return response.data;
          })
          .catch(function (error: AxiosError<OutagesResponse>) {
            return error;
          });
      },
      searchOutages: async (
        searchTerm: string,
        page_size?: number,
        page_number?: number,
        params?: IApiTestParams
      ): Promise<Array<OutageViewModel>> => {
        return this.outagesAxiosInstance({
          method: "GET",
          url: APIUtils.getEndpoint(EApiTypes.SEARCH_OUTAGES, params),
          params: {
            searchvalue: searchTerm,
            page_size: page_size || 50,
            page_number: page_number || 1,
          },
        })
          .then(function (response: AxiosResponse<OutagesResponse>) {
            return response.data;
          })
          .catch(function (error: AxiosError<OutagesResponse>) {
            return error;
          });
      },
      searchAdminOutages: async (
        page_size?: number,
        page_number?: number,
        params?: IApiTestParams
      ): Promise<Array<OutageViewModelWithContacts>> => {
        const authState = store.getState().globalState.auth.entraAuthState;
        const token = authState?.accessToken;
        return this.outagesAxiosInstance({
          method: "GET",
          url: APIUtils.getEndpoint(EApiTypes.ADMIN_OUTAGES, params),
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page_size: page_size || 50,
            page_number: page_number || 1,
          },
        })
          .then(function (response: AxiosResponse<OutagesResponse>) {
            return response.data;
          })
          .catch(function (error: AxiosError<OutagesResponse>) {
            return error;
          });
      },
      updateAdminOutage: async (
        outage: OutageViewModel,
        locationId?: number,
        completedReason?: string,
        params?: IApiTestParams
      ): Promise<Array<OutageViewModelWithContacts>> => {
        const authState = store.getState().globalState.auth.entraAuthState;
        const token = authState?.accessToken;
        let updatePayload = null;
        if (locationId) {
          updatePayload = { ...outage };
        } else if (completedReason) {
          //outage.completedReason = completedReason;
          updatePayload = {
            ...outage,
            completedReason: completedReason,
            location: {
              ...outage.location,
              locationId: 0,
            },
          };
        } else {
          return Promise.reject();
        }

        return this.outagesAxiosInstance({
          method: "PUT",
          url: `outages/${outage.outageId}/adminstatus`,
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: updatePayload,
        })
          .then(function (response: AxiosResponse<OutagesResponse>) {
            return response.data;
          })
          .catch(function (error: AxiosError<OutagesResponse>) {
            return error;
          });
      },
    };
  }

  private initVersionApi(): void {
    this.versionAxiosInstance = this.buildAxiosInstance();
    this._versionApi = {
      getVersion: async (params?: IApiTestParams): Promise<Version> => {
        return this.versionAxiosInstance({
          method: "GET",
          url: APIUtils.getEndpoint(EApiTypes.VERSION, params),
        })
          .then(function (response: AxiosResponse<VersionResponse>) {
            return response.data;
          })
          .catch(function (error: AxiosError<VersionResponse>) {
            return error;
          });
      },
    };
  }

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }

    return ApiService.instance;
  }

  public initializeAPIs(): void {
    this.initLookupsApi();
    this.initOutagesApi();
    this.initVersionApi();
  }

  public set appConfig(appConfig: IAppConfig) {
    this._appConfig = appConfig;
  }

  public get appConfig(): IAppConfig {
    return this._appConfig;
  }

  public get lookupsApi(): ILookUpsAPI {
    return this._lookupsApi;
  }

  public get outagesApi(): IOutagesAPI {
    return this._outagesApi;
  }

  public get versionApi(): IVersionAPI {
    return this._versionApi;
  }
}
